import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {applyThemeToDom} from '@ui/themes/utils/apply-theme-to-dom';
import {
  ThemeId,
  ThemeSelectorContext,
  ThemeSelectorContextValue,
} from '@ui/themes/theme-selector-context';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {themeEl} from '@ui/root-el';

// D36: yeni anahtar → eski "be-active-theme" tercihleri yok sayılır.
// Plan v9.3 pivot: GEÇİŞ DÖNEMİ (D36–D40) public default = DARK (yeni altın
// palet). Cookie yokken dark döner; D41'de 5 sayfa light bitince default
// LIGHT'a flip. Tercih bu anahtarda saklanır (toggle → 365 gün) ve hem client
// hem SSR (BaseBootstrapData) tarafından saygı görür.
const STORAGE_KEY = 'cine5-active-theme';

interface ThemeProviderProps {
  children: any;
}
export function ThemeProvider({children}: ThemeProviderProps) {
  const data = useBootstrapDataStore(s => s.data);
  const allThemes = data.themes;

  const [cookieThemeId, setSelectedThemeId] = useCookie(STORAGE_KEY);
  const [forcedThemeId, setForceThemeId] = useState<ThemeId | null>(null);
  const selectedThemeId = forcedThemeId ?? cookieThemeId;

  const selectThemeTemporarily = useCallback((themeId: ThemeId | null) => {
    setForceThemeId(themeId);
  }, []);

  const getDefaultLightTheme = useCallback(() => {
    return (
      allThemes.find(t => t.default_light && !t.is_dark) ||
      allThemes.find(t => !t.is_dark) ||
      allThemes[0]
    );
  }, [allThemes]);

  const getDefaultDarkTheme = useCallback(() => {
    return (
      allThemes.find(t => t.default_dark || t.is_dark) ||
      allThemes.find(t => t.is_dark) ||
      allThemes[0]
    );
  }, [allThemes]);

  const resolveTheme = useCallback(
    (themeId: ThemeId | null) => {
      // explicit id (toggle / kayıtlı tercih)
      const byId = allThemes.find(t => `${t.id}` === `${themeId}`);
      if (byId) return byId;
      // semantic
      if (themeId === 'dark') return getDefaultDarkTheme();
      if (themeId === 'light') return getDefaultLightTheme();
      // Geçiş dönemi varsayılanı = KARANLIK (Plan v9.3 pivot, D41'de LIGHT'a flip)
      return getDefaultDarkTheme();
    },
    [allThemes, getDefaultLightTheme, getDefaultDarkTheme],
  );

  let selectedTheme = selectedThemeId
    ? resolveTheme(selectedThemeId)
    : getDefaultDarkTheme();

  if (!selectedTheme) {
    selectedTheme = getDefaultDarkTheme()!;
  }

  // if selected theme is different then the one that was set
  // with server render, set new css variables, this will only
  // happen if user has not selected theme manually and default theme is set to "system"
  useEffect(() => {
    const currentThemeId = themeEl.dataset.themeId;
    if (selectedTheme && `${selectedTheme.id}` !== `${currentThemeId}`) {
      applyThemeToDom(selectedTheme);
    }
    // only apply theme if id has changed
  }, [selectedTheme.id]);

  const contextValue: ThemeSelectorContextValue = useMemo(() => {
    return {
      allThemes,
      selectedTheme: selectedTheme!,
      selectThemeTemporarily,
      selectTheme: id => {
        const theme = resolveTheme(id);
        if (theme) {
          setSelectedThemeId(`${theme.id}`, {days: 365});
          applyThemeToDom(theme);
        }
      },
    };
  }, [
    allThemes,
    selectedTheme,
    setSelectedThemeId,
    resolveTheme,
    selectThemeTemporarily,
  ]);

  return (
    <ThemeSelectorContext.Provider value={contextValue}>
      {children}
    </ThemeSelectorContext.Provider>
  );
}
